import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { QcIncidentMessage } from "@/components/qc/QcIncidentMessage";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { useHubToast } from "@/components/ui/HubToast";
import { Input, Label } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { LaborStationOperatorPresence } from "@/components/labor/LaborStationOperatorPresence";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import {
  FLOOR_SCAN_INPUT_FIELD_PROPS,
  floorSerialInputHasScannerTerminator,
  handleFloorSerialEnterKey,
  normalizeFloorSerialInput,
} from "@/lib/floorSerialScan";
import { playPairFailureSound, playPairQuestionSuccessSound, playPairSuccessSound } from "@/lib/floorStationPairAudio";
import { cn } from "@/lib/utils";
import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { playFailureSoundAndNavigate } from "@/lib/labor/laborTimeoutHandlers";
import { useLaborDevStatusPanelRegistration } from "@/lib/labor/laborDevStatusPanelContext";
import type { LaborStatusPanelProps } from "@/lib/labor/LaborStatusPanel";
import { PRE_ENCAPSULATION_LABOR_PAGE } from "@/lib/labor/laborPageKey";
import { useLaborSession } from "@/lib/labor/useLaborSession";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

import { PottingOperatorCartonsPanel, pottingCartonMapKey, type PottingCartonDraftUnit } from "./PottingOperatorCartonsPanel";
import { usePottingZplPrinter } from "./pottingZplPrinterStorage";
import {
  addPreEncapUsaShells,
  fetchPreEncapCartons,
  fetchPreEncapDevice,
  fetchPreEncapDraft,
  fetchPreEncapOpenWorkOrders,
  printPreEncapCarton,
  removePreEncapDraft,
  scanPreEncapDevice,
  sealPreEncapCarton,
  PRE_ENCAP_CARTONS_QUERY_KEY,
  PRE_ENCAP_DRAFT_QUERY_KEY,
  PRE_ENCAP_OPEN_WORK_ORDERS_QUERY_KEY,
  type PreEncapAddUsaShellsResult,
  type PreEncapDevice,
  type PreEncapDraftRow,
  type PreEncapPottingSendFlow,
  type PreEncapScanDraftResult,
} from "./preEncapsulationApi";
import {
  preEncapAlreadyOnDraftMessage,
  preEncapAlreadyAtRouteDestinationMessage,
  preEncapDivertedSuccessMessage,
  preEncapDraftCartonForDevice,
  preEncapIsDivertedSuccessScan,
  preEncapScanFailureMessage,
} from "./preEncapScanFeedback";

const PRE_ENCAP_PAGE_HELP =
  "Scan device labels or clamp serials on the Serialized tab. Internal potting at SGS advances units to the Potting station with no vendor send carton. Finished FI-5A and DM-1 device labels advance to Conformal Coat on scan. On the Unserialized tab, add USA shell quantity rows against open work orders whose finished-goods SKU is on the allowlist in Manufacturing management settings.";

/** Re-enable the SCS vendor-send path toggle on the scan card when external potting returns. */
const PRE_ENCAP_EXTERNAL_POTTING_PATH_UI_ENABLED = false;

/** Five pulses at 0.45s each (see tailwind `carton-success-flash`). */
const CARTON_FLASH_MS = 2400;

/** Scan banners persist until dismissed; constant retained from prior auto-dismiss behavior. */
const SCAN_BANNER_MS = 6_000;

type ScanBanner = { tone: "error" | "warning"; message: string };

function PreEncapsulationLaborTrackerShared() {
  const navigate = useNavigate();
  const labor = useLaborSession({
    page: PRE_ENCAPSULATION_LABOR_PAGE,
    domain: "manufacturing",
    moduleGate: "manufacturing",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled(),
    workLabel: "Pre-Encapsulation",
    autoStart: true,
    requirePersistedClockIn: true,
    onIdleTimeout: () =>
      playFailureSoundAndNavigate(navigate, "/manufacturing", { replace: true }),
    onPersistedSessionLost: () =>
      playFailureSoundAndNavigate(navigate, "/manufacturing", { replace: true }),
    onSessionEndedRemotely: () =>
      playFailureSoundAndNavigate(navigate, "/manufacturing", { replace: true }),
    onStopped: () => playFailureSoundAndNavigate(navigate, "/manufacturing", { replace: true }),
    localOnlyMessage: (reason) =>
      reason === "read-only"
        ? "Started working. Your role is read-only for Manufacturing, so this is a local timer only."
        : "Started working. Labor writes are blocked, so this is a local timer only.",
  });

  const snapshot = useMemo((): LaborStatusPanelProps => {
    return {
      isWorking: labor.isWorking,
      elapsedSeconds: labor.elapsedSeconds,
      workLabel: "Pre-Encapsulation",
      banner: labor.banner,
      lastSummary: labor.lastSummary,
      heartbeatIntervalMs: labor.heartbeatIntervalMs,
      idleTimeoutMs: labor.idleTimeoutMs,
    };
  }, [
    labor.isWorking,
    labor.elapsedSeconds,
    labor.banner,
    labor.lastSummary,
    labor.heartbeatIntervalMs,
    labor.idleTimeoutMs,
  ]);

  useLaborDevStatusPanelRegistration(import.meta.env.DEV, snapshot);
  return null;
}

type PreEncapScanTab = "serialized" | "unserialized";
type PreEncapPottingPath = "scs" | "internal";

const PRE_ENCAP_POTTING_PATH_STORAGE_KEY = "pre-encap-potting-path";

function readStoredPottingPath(): PreEncapPottingPath {
  if (!PRE_ENCAP_EXTERNAL_POTTING_PATH_UI_ENABLED) {
    return "internal";
  }
  try {
    const raw = sessionStorage.getItem(PRE_ENCAP_POTTING_PATH_STORAGE_KEY);
    return raw === "scs" ? "scs" : "internal";
  } catch {
    return "internal";
  }
}

function firstPassFlowForPottingPath(path: PreEncapPottingPath): PreEncapPottingSendFlow {
  return path === "internal" ? "internal_first_pass" : "first_pass";
}

function reworkFlowForPottingPath(path: PreEncapPottingPath): PreEncapPottingSendFlow {
  return path === "internal" ? "internal_rework" : "rework";
}

function cartonMatchesPottingPath(
  flow: PreEncapPottingSendFlow | undefined,
  path: PreEncapPottingPath,
): boolean {
  const normalized = flow ?? "first_pass";
  if (normalized === "rework" || normalized === "internal_rework") {
    return normalized === reworkFlowForPottingPath(path);
  }
  return normalized === firstPassFlowForPottingPath(path);
}

export function PreEncapsulationPage() {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("manufacturing");
  const [serialInput, setSerialInput] = useState("");
  const [scanBanner, setScanBanner] = useState<ScanBanner | null>(null);
  const [usaShellCount, setUsaShellCount] = useState("");
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState("");
  const [selectedWorkOrderLabel, setSelectedWorkOrderLabel] = useState<string | null>(null);
  const [workOrderSearch, setWorkOrderSearch] = useState("");
  const [debouncedWorkOrderSearch, setDebouncedWorkOrderSearch] = useState("");
  const [flashingCartonKey, setFlashingCartonKey] = useState<string | null>(null);
  const [flashingCartonTone, setFlashingCartonTone] = useState<"success" | "warning" | null>(null);
  const { station, setStation, selectedPrinter: zplPrinter, unavailableMessage } =
    usePottingZplPrinter();
  const [scanTab, setScanTab] = useState<PreEncapScanTab>("serialized");
  const [pottingPath, setPottingPath] = useState<PreEncapPottingPath>(() => readStoredPottingPath());
  const [sealPendingCartonKey, setSealPendingCartonKey] = useState<string | null>(null);
  const [printPendingCartonKey, setPrintPendingCartonKey] = useState<string | null>(null);
  const [removePendingDraftId, setRemovePendingDraftId] = useState<number | null>(null);
  const serialInputRef = useRef<HTMLInputElement>(null);
  const serializedScanFormRef = useRef<HTMLFormElement>(null);
  const submitSerializedScanValueRef = useRef<(serial: string) => void>(() => {});
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scanBannerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preserveScanBannerRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedWorkOrderSearch(workOrderSearch);
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [workOrderSearch]);

  const openWorkOrdersQuery = useQuery({
    queryKey: [...PRE_ENCAP_OPEN_WORK_ORDERS_QUERY_KEY, debouncedWorkOrderSearch] as const,
    queryFn: () => fetchPreEncapOpenWorkOrders(debouncedWorkOrderSearch),
    staleTime: 30_000,
    enabled: scanTab === "unserialized",
  });

  const allowedFinishedSkus = openWorkOrdersQuery.data?.allowedFinishedSkus ?? [];

  const openWorkOrderOptions = useMemo(
    () =>
      (openWorkOrdersQuery.data?.workOrders ?? []).map((row) => ({
        value: String(row.id),
        label: row.label,
        description: row.description,
      })),
    [openWorkOrdersQuery.data?.workOrders],
  );

  const openWorkOrderEmptyMessage = useMemo(() => {
    if (openWorkOrdersQuery.error instanceof Error) {
      return openWorkOrdersQuery.error.message;
    }
    if (allowedFinishedSkus.length === 0) {
      return "No allowed finished-goods SKUs are configured. Add SKUs in Pre-Encapsulation settings.";
    }
    return `No open work orders with remaining USA shell capacity match for ${allowedFinishedSkus.join(", ")}.`;
  }, [allowedFinishedSkus, openWorkOrdersQuery.error]);

  const draftQuery = useQuery({
    queryKey: PRE_ENCAP_DRAFT_QUERY_KEY,
    queryFn: fetchPreEncapDraft,
    refetchInterval: 30_000,
  });

  const cartonsQuery = useQuery({
    queryKey: PRE_ENCAP_CARTONS_QUERY_KEY,
    queryFn: fetchPreEncapCartons,
    refetchInterval: 30_000,
  });

  const draftRows = draftQuery.data?.rows ?? [];

  const draftUnitsByCarton = useMemo(() => {
    const grouped: Record<string, PottingCartonDraftUnit[]> = {};
    for (const row of draftRows) {
      if (row.cartonNumber == null) {
        continue;
      }
      const flow = row.sendFlow ?? "first_pass";
      const key = pottingCartonMapKey(flow, row.cartonNumber);
      const unit: PottingCartonDraftUnit = {
        id: row.id,
        serial: row.serial,
        finishedSku: row.finishedSku,
        type: row.type,
      };
      const bucket = grouped[key];
      if (bucket != null) {
        bucket.push(unit);
      } else {
        grouped[key] = [unit];
      }
    }
    for (const units of Object.values(grouped)) {
      units.sort((left, right) => left.id - right.id);
    }
    return grouped;
  }, [draftRows]);

  const visibleOperatorCartons = useMemo(
    () =>
      (cartonsQuery.data?.cartons ?? []).filter((carton) =>
        cartonMatchesPottingPath(carton.flow, pottingPath),
      ),
    [cartonsQuery.data?.cartons, pottingPath],
  );

  const visibleDraftUnitsByCarton = useMemo(() => {
    const filtered: Record<string, PottingCartonDraftUnit[]> = {};
    for (const [key, units] of Object.entries(draftUnitsByCarton)) {
      const flowPrefix = key.split(":")[0] as PreEncapPottingSendFlow;
      if (cartonMatchesPottingPath(flowPrefix, pottingPath)) {
        filtered[key] = units;
      }
    }
    return filtered;
  }, [draftUnitsByCarton, pottingPath]);

  function selectPottingPath(next: PreEncapPottingPath) {
    if (!PRE_ENCAP_EXTERNAL_POTTING_PATH_UI_ENABLED) {
      return;
    }
    setPottingPath(next);
    try {
      sessionStorage.setItem(PRE_ENCAP_POTTING_PATH_STORAGE_KEY, next);
    } catch {
      // sessionStorage may be unavailable
    }
    serialInputRef.current?.focus();
  }

  useFloorPageScanCapture({
    enabled: scanTab === "serialized",
    inputRef: serialInputRef,
    onScan: (serial) => submitSerializedScanValueRef.current(serial),
  });

  useEffect(() => {
    serialInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (scanTab === "serialized") {
      serialInputRef.current?.focus();
    }
  }, [scanTab]);

  function clearScanBannerTimer() {
    if (scanBannerTimeoutRef.current != null) {
      clearTimeout(scanBannerTimeoutRef.current);
      scanBannerTimeoutRef.current = null;
    }
  }

  function clearScanBanner() {
    clearScanBannerTimer();
    preserveScanBannerRef.current = false;
    setScanBanner(null);
  }

  function showScanBanner(tone: ScanBanner["tone"], message: string) {
    void SCAN_BANNER_MS;
    if (tone === "error") {
      playPairFailureSound();
    }
    clearScanBannerTimer();
    setScanBanner({ tone, message });
    preserveScanBannerRef.current = true;
  }

  function showDivertedSuccessBanner(message: string) {
    playPairQuestionSuccessSound();
    showScanBanner("warning", message);
  }

  function flashCarton(
    cartonNumber: number,
    flow: PreEncapPottingSendFlow = "first_pass",
    tone: "success" | "warning" = "success",
  ) {
    if (flashTimeoutRef.current != null) {
      clearTimeout(flashTimeoutRef.current);
    }
    const cartonKey = pottingCartonMapKey(flow, cartonNumber);
    setFlashingCartonKey(null);
    setFlashingCartonTone(null);
    window.requestAnimationFrame(() => {
      setFlashingCartonTone(tone);
      setFlashingCartonKey(cartonKey);
      flashTimeoutRef.current = setTimeout(() => {
        setFlashingCartonKey(null);
        setFlashingCartonTone(null);
        flashTimeoutRef.current = null;
      }, CARTON_FLASH_MS);
    });
  }

  function showAlreadyOnDraftWarning(
    device: PreEncapDevice,
    rows: PreEncapDraftRow[],
    fallbackMessage?: string,
  ) {
    const message = preEncapAlreadyOnDraftMessage(device, rows) ?? fallbackMessage;
    if (message != null && message.trim() !== "") {
      showScanBanner("warning", message);
    }
    const location = preEncapDraftCartonForDevice(device, rows);
    if (location != null) {
      flashCarton(location.cartonNumber, location.flow, "warning");
    }
    finishScan();
  }

  useEffect(() => {
    if (scanBanner == null) {
      return;
    }
    function dismissScanBanner(event: PointerEvent) {
      const target = event.target;
      if (target instanceof Node && serializedScanFormRef.current?.contains(target)) {
        return;
      }
      clearScanBanner();
    }
    document.addEventListener("pointerdown", dismissScanBanner);
    return () => {
      document.removeEventListener("pointerdown", dismissScanBanner);
    };
  }, [scanBanner]);

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current != null) {
        clearTimeout(flashTimeoutRef.current);
      }
      clearScanBannerTimer();
    };
  }, []);

  function finishScan() {
    if (!preserveScanBannerRef.current) {
      clearScanBanner();
    }
    preserveScanBannerRef.current = false;
    serialInputRef.current?.focus();
  }

  function showDraftAddedSuccess(draft: PreEncapScanDraftResult) {
    playPairSuccessSound();
    flashCarton(draft.cartonNumber, draft.flow ?? (draft.kind === "rework" ? "rework" : "first_pass"));
    const cartonPart = draft.productLabel
      ? ` Carton ${draft.cartonNumber} (${draft.productLabel})${draft.createdNewCarton ? " — new box." : "."}`
      : "";
    const reworkPrefix = draft.kind === "rework" ? "Potting rework — assigned " : "Added ";
    const kindLabel =
      draft.kind === "rework"
        ? "device"
        : draft.kind === "device"
          ? "device label"
          : "clamp serial";
    showToast({
      tone: "success",
      message: `${reworkPrefix}${kindLabel} ${draft.serial} to ${draft.kind === "rework" ? "a rework carton" : "the potting draft"}.${cartonPart}${
        draft.laborRecorded ? " Labor recorded." : ""
      }`,
    });
    void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_DRAFT_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_CARTONS_QUERY_KEY });
    finishScan();
  }

  const scanMutation = useMutation({
    mutationFn: async (serial: string) => {
      if (writesAllowed) {
        return scanPreEncapDevice(serial, { addToDraft: true, pottingPath });
      }
      const result = await fetchPreEncapDevice(serial);
      return {
        ok: true,
        device: result.device,
        stationAdvanced: false,
        laborRecorded: false,
        draftAdded: false,
      };
    },
    onSuccess: (result) => {
      const device = result.device;
      const rows = queryClient.getQueryData<{ rows: typeof draftRows }>(PRE_ENCAP_DRAFT_QUERY_KEY)?.rows ?? draftRows;

      if (result.draftAdded && result.draft != null) {
        showDraftAddedSuccess(result.draft);
        return;
      }

      const draftAddError = result.draftAddError?.trim();
      if (draftAddError != null && draftAddError !== "") {
        const alreadyOnDraft =
          draftAddError.includes("already on the potting draft") ||
          draftAddError.includes("already in rework carton") ||
          draftAddError.includes("already assigned to potting rework") ||
          preEncapAlreadyOnDraftMessage(device, rows) != null;
        if (alreadyOnDraft) {
          showAlreadyOnDraftWarning(device, rows, draftAddError);
        } else {
          showScanBanner("error", draftAddError);
        }
        return;
      }

      const alreadyOnDraft = preEncapAlreadyOnDraftMessage(device, rows);
      if (alreadyOnDraft != null) {
        showAlreadyOnDraftWarning(device, rows);
        return;
      }

      const alreadyAtRoute = preEncapAlreadyAtRouteDestinationMessage(device, {
        stationAdvanced: result.stationAdvanced,
      });
      if (alreadyAtRoute != null) {
        showScanBanner("error", alreadyAtRoute);
        finishScan();
        return;
      }

      const failureMessage = preEncapScanFailureMessage(device);
      if (failureMessage != null) {
        showScanBanner("error", failureMessage);
        return;
      }

      if (preEncapIsDivertedSuccessScan(device, { stationAdvanced: result.stationAdvanced })) {
        showDivertedSuccessBanner(
          preEncapDivertedSuccessMessage(device, { stationAdvanced: result.stationAdvanced }),
        );
        finishScan();
        return;
      }

      playPairSuccessSound();
      finishScan();
    },
    onError: (mutationError: Error) => {
      showScanBanner("error", mutationError.message);
    },
  });

  function readSerializedScanValue(): string {
    const raw = serialInputRef.current?.value ?? serialInput;
    return normalizeFloorSerialInput(raw);
  }

  function beginScan(nextSerial: string) {
    if (scanMutation.isPending) {
      return;
    }
    clearScanBanner();
    setSerialInput("");
    if (serialInputRef.current != null) {
      serialInputRef.current.value = "";
    }
    serialInputRef.current?.focus();
    scanMutation.mutate(nextSerial);
  }

  submitSerializedScanValueRef.current = beginScan;

  function submitSerializedScan(event?: { preventDefault(): void }) {
    event?.preventDefault();
    const nextSerial = readSerializedScanValue();
    if (nextSerial === "") {
      return;
    }
    beginScan(nextSerial);
  }

  const usaShellMutation = useMutation({
    mutationFn: (input: { count: number; buildOrderId: number; pottingPath: PreEncapPottingPath }) =>
      addPreEncapUsaShells(input),
    onSuccess: (data: PreEncapAddUsaShellsResult) => {
      playPairSuccessSound();
      if (data.cartonNumber != null) {
        flashCarton(data.cartonNumber, firstPassFlowForPottingPath(pottingPath));
      }
      const cartonPart =
        data.cartonNumber != null && data.productLabel
          ? ` Carton ${data.cartonNumber} (${data.productLabel}).`
          : "";
      showToast({
        tone: "success",
        message: `Added ${data.count} FI-5A USA shell row(s) for ${selectedWorkOrderLabel ?? `work order #${data.buildOrderId}`}.${cartonPart}${
          data.laborRecorded ? " Labor recorded." : ""
        }`,
      });
      setUsaShellCount("");
      setSelectedWorkOrderId("");
      setSelectedWorkOrderLabel(null);
      setWorkOrderSearch("");
      void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_DRAFT_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_CARTONS_QUERY_KEY });
    },
    onError: (mutationError: Error) => {
      showScanBanner("error", mutationError.message);
    },
  });

  const sealCartonMutation = useMutation({
    mutationFn: (input: { cartonNumber: number; flow: PreEncapPottingSendFlow }) =>
      sealPreEncapCarton(input.cartonNumber, input.flow),
    onMutate: (input) => {
      setSealPendingCartonKey(pottingCartonMapKey(input.flow, input.cartonNumber));
    },
    onSettled: () => {
      setSealPendingCartonKey(null);
    },
    onSuccess: (_data, input) => {
      const carton = cartonsQuery.data?.cartons.find(
        (row) => row.cartonNumber === input.cartonNumber && row.flow === input.flow,
      );
      showToast({
        tone: "success",
        message: carton
          ? `Carton ${input.cartonNumber} sealed (${carton.rowCount} × ${carton.productLabel}). Print its label when ready — it will leave this list after printing.`
          : `Carton ${input.cartonNumber} sealed. Print its label when ready.`,
      });
      void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_CARTONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_DRAFT_QUERY_KEY });
    },
    onError: (mutationError: Error) => {
      showScanBanner("error", mutationError.message);
    },
  });

  const printCartonMutation = useMutation({
    mutationFn: (input: { cartonNumber: number; flow: PreEncapPottingSendFlow }) =>
      printPreEncapCarton(input.cartonNumber, zplPrinter.trim(), input.flow),
    onMutate: (input) => {
      setPrintPendingCartonKey(pottingCartonMapKey(input.flow, input.cartonNumber));
    },
    onSettled: () => {
      setPrintPendingCartonKey(null);
    },
    onSuccess: (data, input) => {
      showToast({
        tone: "success",
        message: `Sent carton ${input.cartonNumber} label to the printer (${data.lineCount} line group${data.lineCount === 1 ? "" : "s"}).`,
      });
      void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_CARTONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_DRAFT_QUERY_KEY });
    },
    onError: (mutationError: Error) => {
      showScanBanner("error", mutationError.message);
    },
  });

  const removeDraftMutation = useMutation({
    mutationFn: (id: number) => removePreEncapDraft(id),
    onMutate: (id) => {
      setRemovePendingDraftId(id);
    },
    onSettled: () => {
      setRemovePendingDraftId(null);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_DRAFT_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: PRE_ENCAP_CARTONS_QUERY_KEY });
      serialInputRef.current?.focus();
    },
    onError: (mutationError: Error) => {
      showScanBanner("error", mutationError.message);
    },
  });

  const isScanPending = scanMutation.isPending;
  const isAddingUsaShells = usaShellMutation.isPending;
  const isSealingCarton = sealCartonMutation.isPending;
  const isPrintingCarton = printCartonMutation.isPending;
  const isMutating =
    isScanPending ||
    isAddingUsaShells ||
    isSealingCarton ||
    isPrintingCarton ||
    removeDraftMutation.isPending;
  const operatorCartons = visibleOperatorCartons;

  function handleAddUsaShells() {
    const count = Number.parseInt(usaShellCount.trim(), 10);
    const buildOrderId = Number.parseInt(selectedWorkOrderId.trim(), 10);
    if (!Number.isFinite(count) || count <= 0) {
      showScanBanner("error", "USA shell quantity must be a positive integer.");
      return;
    }
    if (!Number.isFinite(buildOrderId) || buildOrderId <= 0) {
      showScanBanner("error", "Select an open work order.");
      return;
    }
    usaShellMutation.mutate({ count, buildOrderId, pottingPath });
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <PageTitleWithHelp hoverHint={PRE_ENCAP_PAGE_HELP}>Pre-Encapsulation</PageTitleWithHelp>
        </div>
        <LaborStationOperatorPresence stationPage="pre-encapsulation" />
      </header>

      <PreEncapsulationLaborTrackerShared />

      <Card>
        <CardHeader>
          <CardTitle>Scan</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              value={scanTab}
              onChange={setScanTab}
              options={[
                { value: "serialized", label: "Serialized" },
                { value: "unserialized", label: "Unserialized" },
              ]}
              aria-label="Scan mode"
            />
            {PRE_ENCAP_EXTERNAL_POTTING_PATH_UI_ENABLED ? (
              <>
                <div className="h-6 w-px shrink-0 bg-slate-300" aria-hidden />
                <SegmentedControl
                  value={pottingPath}
                  onChange={selectPottingPath}
                  options={[
                    { value: "scs", label: "SCS" },
                    { value: "internal", label: "Internal" },
                  ]}
                  aria-label="Potting path"
                />
              </>
            ) : null}
          </div>

          {scanTab === "serialized" ? (
            <>
              <form
                ref={serializedScanFormRef}
                className="flex flex-col gap-2 sm:flex-row"
                onSubmit={submitSerializedScan}
              >
                <Input
                  ref={serialInputRef}
                  name="serial"
                  value={serialInput}
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (scanBanner != null) {
                      clearScanBanner();
                    }
                    if (floorSerialInputHasScannerTerminator(raw)) {
                      const nextSerial = normalizeFloorSerialInput(raw);
                      setSerialInput("");
                      if (serialInputRef.current != null) {
                        serialInputRef.current.value = "";
                      }
                      if (nextSerial !== "") {
                        beginScan(nextSerial);
                      }
                      return;
                    }
                    setSerialInput(raw);
                  }}
                  onKeyDown={(event) => handleFloorSerialEnterKey(event, submitSerializedScan)}
                  placeholder="Serial"
                  {...FLOOR_SCAN_INPUT_FIELD_PROPS}
                  {...FLOOR_PAGE_SCAN_TARGET_PROPS}
                />
                <Button type="submit" className="min-h-12 shrink-0 px-6 text-base" isLoading={isScanPending}>
                  Submit
                </Button>
              </form>
              {scanBanner != null ? (
                <p
                  role="alert"
                  aria-live="assertive"
                  className={cn(
                    "whitespace-pre-line rounded-md border px-3 py-2 text-sm font-medium",
                    scanBanner.tone === "error"
                      ? "border-rose-200 bg-rose-50 text-rose-900"
                      : "border-amber-200 bg-amber-50 text-amber-900",
                  )}
                >
                  {scanBanner.message.split("\n").map((line, index) => (
                    <span key={`scan-banner-line-${index}`}>
                      {index > 0 ? <br /> : null}
                      <QcIncidentMessage message={line} />
                    </span>
                  ))}
                </p>
              ) : null}
            </>
          ) : (
            <form
              className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto]"
              onSubmit={(event) => {
                event.preventDefault();
                handleAddUsaShells();
              }}
            >
              <div className="space-y-1">
                <Label htmlFor="pre-encap-usa-shell-count">Quantity</Label>
                <Input
                  id="pre-encap-usa-shell-count"
                  type="number"
                  min={1}
                  step={1}
                  value={usaShellCount}
                  onChange={(event) => setUsaShellCount(event.target.value)}
                  placeholder="Shell count"
                  disabled={!writesAllowed || isMutating}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="pre-encap-usa-shell-work-order">Work order</Label>
                <SearchableSelect
                  id="pre-encap-usa-shell-work-order"
                  presentation="inlineFilter"
                  value={selectedWorkOrderId}
                  selectedLabelOverride={selectedWorkOrderLabel}
                  onValueChange={(option) => {
                    setSelectedWorkOrderId(option?.value ?? "");
                    setSelectedWorkOrderLabel(option?.label ?? null);
                    if (option == null) {
                      setWorkOrderSearch("");
                    }
                  }}
                  options={openWorkOrderOptions}
                  searchValue={workOrderSearch}
                  onSearchChange={setWorkOrderSearch}
                  placeholder="Search SBI or SBO number…"
                  searchPlaceholder="Search work order"
                  emptyMessage={openWorkOrderEmptyMessage}
                  isLoading={openWorkOrdersQuery.isFetching}
                  disabled={!writesAllowed || isMutating}
                  panelMinWidth={320}
                />
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={!writesAllowed || isMutating} isLoading={isAddingUsaShells}>
                  Add USA shells
                </Button>
              </div>
            </form>
          )}
        </CardBody>
      </Card>

      <PottingOperatorCartonsPanel
        cartons={operatorCartons}
        writesAllowed={writesAllowed}
        busy={isMutating}
        layout="columns"
        flashingCartonKey={flashingCartonKey}
        flashingCartonTone={flashingCartonTone}
        draftUnitsByCarton={visibleDraftUnitsByCarton}
        allowRemoveDraftUnits
        removePendingDraftId={removePendingDraftId}
        onRemoveDraftUnit={(id) => removeDraftMutation.mutate(id)}
        sealPendingCartonKey={sealPendingCartonKey}
        printPendingCartonKey={printPendingCartonKey}
        onSeal={(carton) =>
          sealCartonMutation.mutate({
            cartonNumber: carton.cartonNumber,
            flow: carton.flow ?? "first_pass",
          })
        }
        onPrint={(carton) =>
          printCartonMutation.mutate({
            cartonNumber: carton.cartonNumber,
            flow: carton.flow ?? "first_pass",
          })
        }
        zplPrinter={zplPrinter}
        station={station}
        onStationChange={setStation}
        unavailableMessage={unavailableMessage}
      />
    </div>
  );
}
